// SkipJobEditPage — /skip/jobs/:id/edit.
//
// Pre-populated form. Status-aware locking: completed/cancelled jobs
// are read-only with a banner; delivered+ states lock delivery_date.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import {
  useSkipJob,
  useSkipSizes,
  useUpdateSkipJob,
} from '../hooks/useSkipJobs';
import { useActivatedLegacySkipTypes } from '../hooks/useActivatedLegacySkipTypes';
import { useSkipJobTypes } from '../hooks/useSkipJobTypes';
import { useSkipInventory } from '../hooks/useSkipInventory';
import { useDriversList } from '../../admin/drivers/hooks/useDriversAdmin';
import { AttachmentSection } from '../components/AttachmentSection';
import {
  SitePicker,
  type SitePickerValue,
} from '../../../components/ui/SitePicker/SitePicker';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './SkipJobEditPage.module.css';

const LEGACY_LABELS: Readonly<Record<string, string>> = {
  skip: 'Skip',
  bin: 'Bin',
  roro: 'Roro',
};

interface FormState {
  customer_name: string;
  customer_email: string | null;
  customer_phone: string;
  customer_postcode: string;
  skip_type: 'skip' | 'bin' | 'roro' | 'other';
  /** Bug 2 — when the user picks a custom (non-legacy) stock type
   *  from the dropdown, this carries its enc_id so the size dropdown
   *  filters by parent stock type. skip_type stays 'other' on the
   *  persisted row (the legacy ENUM has no slot for customs). Null
   *  when a legacy type ('skip'|'bin'|'roro'|'other') is selected. */
  custom_stock_type_uuid: string | null;
  /** Encrypted enc_id of the chosen skip_stock_sizes row. Server's
   *  /reference/skip-sizes catalogue returns enc_ids and the form
   *  binds them as strings (Number(enc_id) is NaN). */
  skip_size_enc_id: string | null;
  /** enc_id of the selected skip_job_types row. Null when no type is
   *  selected; the server-side updateJobSchema accepts this field
   *  (Phase 6E) and resolves it to skip_job_type_id. */
  skip_job_type_enc_id: string | null;
  skip_inventory_uuid: string | null;
  /** Feature 1 — assigned driver. uuid of a drivers row. Null when
   *  unassigned. The PUT payload sends it as `driver_uuid`. */
  driver_uuid: string | null;
  is_priority: boolean;
  priority_notes: string;
  delivery_date: string;
  delivery_time: string;
  notes: string;
  /** Phase 6D — SitePicker emits a SitePickerValue; we store the
   *  resolved fields directly so the PATCH payload is built without
   *  any extra transformation. */
  site_enc_id: string | null;
  job_address_line1: string | null;
  job_address_line2: string | null;
  job_address_city: string | null;
  job_address_postcode: string | null;
}

export function SkipJobEditPage(): JSX.Element {
  const { id: routeId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const jobQuery = useSkipJob(routeId);
  const sizesQuery = useSkipSizes();
  const activatedLegacy = useActivatedLegacySkipTypes();
  const jobTypesQuery = useSkipJobTypes({ forSelection: true });
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bug 3 — server-filter inventory by the form's chosen size so the
  // dropdown only lists units that actually match. The picker stays
  // disabled until a size is chosen.
  const inventoryQuery = useSkipInventory(
    form?.skip_size_enc_id != null
      ? { status: 'available', limit: 100, skip_size_enc_id: form.skip_size_enc_id }
      : { status: 'available', limit: 100 },
  );
  // Feature 1 — drivers list for the Assign Driver dropdown.
  const driversQuery = useDriversList({ page: 1, limit: 100, status: 'active' });
  const drivers = driversQuery.data?.drivers ?? [];
  // The route may carry either an enc_id (canonical) or a raw uuid
  // (callers like SkipInventory* and SkipEnquiryDetailPage still link
  // with raw uuids). The server tolerates both, but we prefer the
  // job's own enc_id for outbound API calls so URLs stay consistent.
  const stableEncId = jobQuery.data?.job.enc_id ?? routeId;
  const updateJob = useUpdateSkipJob(stableEncId);

  const jobTypes = jobTypesQuery.data?.jobTypes ?? [];

  // Saved jobs may carry a skip_type whose underlying global is now
  // deactivated. We *include* that saved value in the dropdown so the
  // user sees what the job currently is and can edit other fields
  // without losing the saved value (the read-only detail view does
  // the same). The 'other' catch-all is always available.
  const typeOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string; isCustom?: boolean }> =
      activatedLegacy.types.map((t) => ({
        value: t,
        label: LEGACY_LABELS[t] ?? t,
      }));
    const saved = jobQuery.data?.job.skip_type;
    if (saved && saved !== 'other' && !activatedLegacy.types.includes(saved as 'skip' | 'bin' | 'roro')) {
      opts.push({ value: saved, label: `${LEGACY_LABELS[saved] ?? saved} (deactivated)` });
    }
    // Bug 2 — own-tenant custom stock types appear alongside the
    // legacy globals. Each custom encodes its enc_id in the option
    // value as `custom:${enc_id}` so the change handler can reliably
    // distinguish customs from legacy.
    for (const c of activatedLegacy.customTypes) {
      opts.push({ value: `custom:${c.uuid}`, label: c.name, isCustom: true });
    }
    opts.push({ value: 'other', label: 'Other' });
    return opts;
  }, [activatedLegacy.types, activatedLegacy.customTypes, jobQuery.data]);

  useEffect(() => {
    const job = jobQuery.data?.job;
    if (!job || form !== null) return;
    // Resolve the saved job_type key to its skip_job_types row enc_id
    // so the dropdown pre-selects correctly and the PUT carries the
    // FK reference the server expects.
    const matched = jobTypes.find((t) => t.key === job.job_type);
    setForm(() => ({
      customer_name: job.customer_name,
      customer_email: job.customer_email,
      customer_phone: job.customer_phone,
      customer_postcode: job.customer_postcode,
      skip_type: job.skip_type,
      // If the saved row references a custom stock type (legacy
      // skip_type='other' + a non-null skip_stock_type_uuid), pre-
      // load the custom uuid so the dropdown shows the real name
      // and the size filter scopes by parent stock type.
      custom_stock_type_uuid:
        job.skip_type === 'other' && job.skip_stock_type_uuid != null
          ? job.skip_stock_type_uuid
          : null,
      skip_size_enc_id: job.skip_size_enc_id,
      skip_job_type_enc_id: matched?.enc_id ?? null,
      skip_inventory_uuid: job.skip_inventory_uuid,
      driver_uuid: job.driver_uuid,
      is_priority: (job as typeof job & { is_priority?: boolean }).is_priority === true,
      priority_notes:
        (job as typeof job & { priority_notes?: string | null }).priority_notes ?? '',
      delivery_date: job.delivery_date ?? '',
      delivery_time: job.delivery_time ?? '',
      notes: job.notes ?? '',
      // Bug 1 — pre-fill site_enc_id when the job has a saved site
      // assigned. SitePicker also receives initialSiteEncId so the
      // dropdown shows the saved site selected on first render.
      site_enc_id: job.site_enc_id ?? null,
      job_address_line1: job.job_address_line1 ?? null,
      job_address_line2: job.job_address_line2 ?? null,
      job_address_city: job.job_city ?? null,
      job_address_postcode: job.job_postcode ?? null,
    }));
  }, [jobQuery.data, jobTypes, form]);

  if (jobQuery.isLoading || form === null) {
    return (
      <div className={styles.page}>
        <PageHeader title="Loading…" />
      </div>
    );
  }

  if (jobQuery.isError || !jobQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Job not found" />
        <Card>
          <p>This job could not be loaded.</p>
        </Card>
      </div>
    );
  }

  const job = jobQuery.data.job;
  const isTerminal = job.status === 'completed' || job.status === 'cancelled';
  const deliveryLocked =
    job.status === 'delivered' ||
    job.status === 'awaiting_collection' ||
    job.status === 'collected';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form === null) return;
    setError(null);
    const body: Record<string, unknown> = {
      customer_name: form.customer_name,
      customer_email: form.customer_email,
      customer_phone: form.customer_phone,
      customer_postcode: form.customer_postcode,
      skip_type: form.skip_type,
      notes: form.notes || null,
      is_priority: form.is_priority,
      priority_notes:
        form.is_priority && form.priority_notes.trim()
          ? form.priority_notes.trim()
          : null,
    };
    if (form.skip_size_enc_id !== null) body['skip_size_enc_id'] = form.skip_size_enc_id;
    if (form.skip_inventory_uuid !== null) {
      body['skip_inventory_uuid'] = form.skip_inventory_uuid;
    }
    // Feature 1 — driver assignment. Always send (including null) so
    // the user can clear an assignment back to unassigned.
    body['driver_uuid'] = form.driver_uuid;
    if (form.skip_job_type_enc_id !== null) {
      body['skip_job_type_enc_id'] = form.skip_job_type_enc_id;
    }
    // Phase 6D — only send site fields when the picker actually
    // emitted a change (otherwise leave the existing site_id untouched).
    if (form.site_enc_id !== null) {
      body['site_enc_id'] = form.site_enc_id;
    } else if (form.job_address_line1 && form.job_address_postcode) {
      body['site_enc_id'] = null;
      body['job_address_line1'] = form.job_address_line1;
      body['job_address_line2'] = form.job_address_line2;
      body['job_city'] = form.job_address_city;
      body['job_postcode'] = form.job_address_postcode;
      body['use_different_job_address'] = true;
    }
    if (!deliveryLocked) {
      body['delivery_date'] = form.delivery_date || null;
      body['delivery_time'] = form.delivery_time || null;
    }
    updateJob.mutate(body, {
      onSuccess: () => navigate(`/skip/jobs/${encodeURIComponent(stableEncId)}`),
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Edit Job ${job.job_number}`}
        subtitle="Update job details"
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate(`/skip/jobs/${encodeURIComponent(stableEncId)}`)}
          >
            Cancel
          </Button>
        }
      />

      {isTerminal ? (
        <Card>
          <p className={styles.banner}>
            This job is <strong>{job.status}</strong> and cannot be edited.
          </p>
        </Card>
      ) : null}

      <form onSubmit={handleSubmit} className={styles.form}>
        <fieldset className={styles.fieldset} disabled={isTerminal}>
          <Card>
            <h2 className={styles.sectionHeading}>Customer</h2>
            <div className={styles.customerDisplay}>
              <div className={styles.customerInfo}>
                <span className={styles.customerName}>{form.customer_name}</span>
                {form.customer_phone && (
                  <span className={styles.customerMeta}>{form.customer_phone}</span>
                )}
                {form.customer_email && (
                  <span className={styles.customerMeta}>{form.customer_email}</span>
                )}
                {form.customer_postcode && (
                  <span className={styles.customerMeta}>{form.customer_postcode}</span>
                )}
              </div>
              <span className={styles.customerLocked}>
                Customer cannot be changed after job creation
              </span>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Job Site</h2>
            <SitePicker
              customerEncId={job.customer_uuid}
              initialSiteUuid={job.site_uuid}
              initialJobAddress={{
                line1: job.job_address_line1,
                line2: job.job_address_line2,
                city: job.job_city,
                postcode: job.job_postcode,
              }}
              onChange={(value: SitePickerValue) => {
                if (value.type === 'site') {
                  setForm((prev) =>
                    prev === null
                      ? prev
                      : {
                          ...prev,
                          site_enc_id: value.site_enc_id,
                          job_address_line1: null,
                          job_address_line2: null,
                          job_address_city: null,
                          job_address_postcode: null,
                        },
                  );
                } else if (value.type === 'one_off') {
                  setForm((prev) =>
                    prev === null
                      ? prev
                      : {
                          ...prev,
                          site_enc_id: null,
                          job_address_line1: value.line1,
                          job_address_line2: value.line2,
                          job_address_city: value.city,
                          job_address_postcode: value.postcode,
                        },
                  );
                }
              }}
            />
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Stock Details</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Stock Type</span>
                <select
                  value={
                    form.custom_stock_type_uuid !== null
                      ? `custom:${form.custom_stock_type_uuid}`
                      : form.skip_type
                  }
                  onChange={(e) => {
                    // Type → reset size: the size set is scoped to the
                    // type, so a stale id from the prior type would be
                    // wrong for the new selection.
                    const v = e.target.value;
                    if (v.startsWith('custom:')) {
                      const uuid = v.slice('custom:'.length);
                      setForm((prev) =>
                        prev === null
                          ? prev
                          : {
                              ...prev,
                              skip_type: 'other',
                              custom_stock_type_uuid: uuid,
                              skip_size_enc_id: null,
                            },
                      );
                    } else {
                      setForm((prev) =>
                        prev === null
                          ? prev
                          : {
                              ...prev,
                              skip_type: v as FormState['skip_type'],
                              custom_stock_type_uuid: null,
                              skip_size_enc_id: null,
                            },
                      );
                    }
                  }}
                >
                  {typeOptions.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Stock Size</span>
                <select
                  value={form.skip_size_enc_id ?? ''}
                  onChange={(e) => {
                    const nextSize = e.target.value === '' ? null : e.target.value;
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : { ...prev, skip_size_enc_id: nextSize },
                    );
                  }}
                >
                  <option value="">— Select size —</option>
                  {(sizesQuery.data?.skipSizes ?? [])
                    .filter((s) =>
                      form.custom_stock_type_uuid !== null
                        ? s.stock_type_uuid === form.custom_stock_type_uuid
                        : s.skip_type === form.skip_type,
                    )
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Job Type</span>
                <select
                  value={form.skip_job_type_enc_id ?? ''}
                  onChange={(e) => {
                    const encIdValue = e.target.value || null;
                    setForm((prev) =>
                      prev === null
                        ? prev
                        : {
                            ...prev,
                            skip_job_type_enc_id: encIdValue,
                          },
                    );
                  }}
                >
                  {jobTypes.length === 0 ? (
                    <option value="">— Loading types —</option>
                  ) : null}
                  {jobTypes.map((t) => (
                    <option key={t.enc_id} value={t.enc_id}>
                      {t.name}
                      {t.process_immediately ? ' (Immediate)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Assign Stock Unit</h2>
            <select
              value={form.skip_inventory_uuid ?? ''}
              onChange={(e) =>
                setForm((prev) =>
                  prev === null
                    ? prev
                    : { ...prev, skip_inventory_uuid: e.target.value || null },
                )
              }
              disabled={form.skip_size_enc_id === null}
            >
              <option value="">
                {form.skip_size_enc_id === null
                  ? '— Select stock type and size first —'
                  : '— No unit assigned —'}
              </option>
              {(inventoryQuery.data?.inventory ?? []).map((u) => (
                <option key={u.enc_id} value={u.uuid}>
                  {u.skip_number}
                  {u.stock_type_name ? ` — ${u.stock_type_name}` : ''}
                  {u.size_name ? ` ${u.size_name}` : ''}
                </option>
              ))}
            </select>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Assign Driver</h2>
            <select
              value={form.driver_uuid ?? ''}
              onChange={(e) =>
                setForm((prev) =>
                  prev === null
                    ? prev
                    : { ...prev, driver_uuid: e.target.value || null },
                )
              }
            >
              <option value="">— No driver assigned —</option>
              {drivers.map((d) => (
                <option key={d.uuid} value={d.uuid}>
                  {d.name}
                </option>
              ))}
            </select>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Priority</h2>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.is_priority}
                onChange={(e) =>
                  setForm((prev) =>
                    prev === null
                      ? prev
                      : {
                          ...prev,
                          is_priority: e.target.checked,
                          priority_notes: e.target.checked ? prev.priority_notes : '',
                        },
                  )
                }
              />
              <span>Mark as Priority</span>
            </label>
            {form.is_priority ? (
              <label className={styles.field} style={{ marginTop: '0.5rem' }}>
                <span>Priority Notes</span>
                <textarea
                  rows={2}
                  value={form.priority_notes}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, priority_notes: e.target.value },
                    )
                  }
                  maxLength={5000}
                  placeholder="Why is this priority?"
                />
              </label>
            ) : null}
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Delivery</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Delivery Date{deliveryLocked ? ' (locked)' : ''}</span>
                <input
                  type="date"
                  value={form.delivery_date}
                  disabled={deliveryLocked}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, delivery_date: e.target.value },
                    )
                  }
                />
              </label>
              <label className={styles.field}>
                <span>Delivery Time{deliveryLocked ? ' (locked)' : ''}</span>
                <input
                  type="text"
                  value={form.delivery_time}
                  disabled={deliveryLocked}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev === null ? prev : { ...prev, delivery_time: e.target.value },
                    )
                  }
                />
              </label>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Notes</h2>
            <textarea
              className={styles.textarea}
              rows={4}
              value={form.notes}
              onChange={(e) =>
                setForm((prev) =>
                  prev === null ? prev : { ...prev, notes: e.target.value },
                )
              }
            />
          </Card>

        </fieldset>

        <Card>
          <h2 className={styles.sectionHeading}>Attachments</h2>
          <AttachmentSection
            kind="job"
            encId={stableEncId}
            readOnly={isTerminal}
          />
        </Card>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/skip/jobs/${encodeURIComponent(stableEncId)}`)}
            disabled={updateJob.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={updateJob.isPending || isTerminal}>
            {updateJob.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
